import { useMemo, useState } from "react";
import toast from "react-hot-toast";
import { useAdminCRUD, useModal, useConfirm } from "../../hooks/useAdmin";
import { Button } from "../shared/Button";
import { Input } from "../shared/Input";
import { Card } from "../shared/Card";
import { Table } from "../shared/Table";
import { Badge } from "../shared/Badge";
import { Loading } from "../shared/Loading";
import { ErrorMessage } from "../shared/ErrorMessage";
import { Modal } from "../shared/Modal";
import { validationRules } from "../../utils/validation";
import { cleanDni, formatDni } from "../../utils/formatDni";
import * as adminApi from "../../services/api";

export default function UsuariosSection({ token, primaryColor = "#6366F1" }) {
  const {
    items: usuarios,
    loading,
    error,
    submitting,
    reload,
    create,
    update,
    remove,
  } = useAdminCRUD(
    {
      fetchAll: adminApi.listarUsuarios,
      create: adminApi.crearUsuario,
      update: adminApi.editarUsuario,
      remove: adminApi.eliminarUsuario,
    },
    token
  );

  const formModal = useModal();
  const { confirmAsync } = useConfirm();

  const [searchTerm, setSearchTerm] = useState("");
  const [formData, setFormData] = useState({
    username: "",
    email: "",
    password: "",
    first_name: "",
    last_name: "",
    dni: "",
    telefono: "",
  });
  const [formErrors, setFormErrors] = useState({});

  const isEditing = !!formModal.data;

  // -------- Filtering (HOOK SIEMPRE ARRIBA)
  const usuariosFiltrados = useMemo(() => {
    if (!searchTerm.trim()) return usuarios;

    const term = searchTerm.toLowerCase();

    return usuarios.filter((u) => {
      const username = (u.username || "").toLowerCase();
      const email = (u.email || "").toLowerCase();
      const first = (u.first_name || "").toLowerCase();
      const last = (u.last_name || "").toLowerCase();
      const tel = (u.telefono || "").toLowerCase();
      const full = `${first} ${last}`.trim();
      

      return (
        username.includes(term) ||
        email.includes(term) ||
        full.includes(term) ||
        tel.includes(term)
      );
    });
  }, [usuarios, searchTerm]);

  // -------- Results label 
  const resultsLabel = useMemo(() => {
    if (!searchTerm.trim()) return `Total: ${usuarios.length}`;
    if (usuariosFiltrados.length === 0) return "Sin resultados";
    if (usuariosFiltrados.length === usuarios.length) return `Mostrando todos (${usuarios.length})`;
    return `Mostrando ${usuariosFiltrados.length} de ${usuarios.length}`;
  }, [searchTerm, usuarios.length, usuariosFiltrados.length]);

  // -------- Table columns 
  const columns = useMemo(
    () => [
      {
        key: "username",
        header: "Usuario",
        render: (value) => <span className="font-medium text-white">{value}</span>,
      },
      {
        key: "nombre",
        header: "Nombre",
        render: (_, row) => `${row.first_name || ""} ${row.last_name || ""}`.trim() || "-",
      },
      { key: "dni", header: "DNI", render: (value) => value || <span className="text-slate-500">—</span> },
      { key: "email", header: "Email", render: (value) => value || "-" },
      { key: "telefono", header: "Teléfono", render: (value) => value || "-" },
      {
        key: "suscripcion_activa",
        header: "Suscripción",
        render: (suscripcion) => {
          if (!suscripcion) return <Badge variant="danger">Sin suscripción</Badge>;
          return (
            <div className="flex flex-col">
              <Badge variant="success">{suscripcion.plan?.nombre || "Activa"}</Badge>
              <span className="text-xs text-slate-400 mt-1">
                {suscripcion.creditos_restantes ?? 0} créditos
              </span>
            </div>
          );
        },
      },
      {
        key: "clases_reservadas",
        header: "Clases",
        align: "text-center",
        render: (value) => <span className="text-slate-200">{value} reservadas</span>,
      },
      {
        key: "actions",
        header: "Acciones",
        align: "text-center",
        render: (_, row) => (
          <div className="flex justify-center gap-2">
            <button
              onClick={(e) => {
                e.stopPropagation();
                handleEdit(row);
              }}
              className="inline-flex items-center justify-center rounded-xl border border-slate-700 bg-slate-800/30 px-3 py-1.5 text-xs font-medium text-slate-200
                         hover:bg-slate-800/50 hover:text-white focus:outline-none focus:ring-2"
              style={{ "--tw-ring-color": primaryColor }}
            >
              Editar
            </button>

            <button
              onClick={(e) => {
                e.stopPropagation();
                handleDelete(row);
              }}
              className="inline-flex items-center justify-center rounded-xl border border-red-500/30 bg-red-600/10 px-3 py-1.5 text-xs font-medium text-red-300
                         hover:bg-red-600/20 hover:text-red-200 focus:outline-none focus:ring-2 focus:ring-red-500/30"
            >
              Desactivar
            </button>
          </div>
        ),
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [primaryColor, isEditing, formModal.data, usuariosFiltrados.length, usuarios.length]
  );

  // -------- Validation
  const validateForm = () => {
  const errors = {};

  const username = formData.username?.trim();
  const email = formData.email?.trim();

  if (!username) errors.username = "El usuario es requerido";
  if (!isEditing && !formData.password) errors.password = "La contraseña es requerida";

  const dni = formData.dni?.trim();
  if (!dni) errors.dni = "El DNI es requerido";
  else if (!/^\d{6,10}$/.test(dni)) errors.dni = "DNI inválido (solo números, 6 a 10 dígitos)";

  if (email) {
    const emailValidation = validationRules.email(email);

    if (typeof emailValidation === "string") {
      errors.email = emailValidation;
    }
    else if (emailValidation === false) {
      errors.email = "Email inválido";
    }
    
  }

  setFormErrors(errors);
  return Object.keys(errors).length === 0;
};


  const handleInputChange = (name, value) => {
    const nextValue = name === "dni" ? cleanDni(value) : value;
    setFormData((prev) => ({ ...prev, [name]: nextValue }));
    if (formErrors[name]) setFormErrors((prev) => ({ ...prev, [name]: null }));
  };

  const resetForm = () => {
    setFormData({
      username: "",
      email: "",
      password: "",
      first_name: "",
      last_name: "",
      dni: "",
      telefono: "",
    });
    setFormErrors({});
  };

  const handleCreate = () => {
    formModal.open();
    resetForm();
  };

  const handleEdit = (usuario) => {
    formModal.open(usuario);
    setFormData({
      username: usuario.username || "",
      email: usuario.email || "",
      password: "",
      first_name: usuario.first_name || "",
      last_name: usuario.last_name || "",
      dni: usuario.dni || "",
      telefono: usuario.telefono || "",
    });
    setFormErrors({});
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      toast.error("Revisá los campos marcados", { icon: "⚠️", duration: 3500 });
      return;
    }

    const loadingToast = toast.loading(isEditing ? "Actualizando usuario..." : "Creando usuario...", {
      style: { borderLeft: `4px solid ${primaryColor}` },
    });

    try {
      const payload = {
        ...formData,
        username: formData.username?.trim(),
        email: formData.email?.trim(),
        first_name: formData.first_name?.trim(),
        last_name: formData.last_name?.trim(),
        dni: formData.dni?.trim(),
        telefono: formData.telefono?.trim(),
      };

      if (isEditing && !payload.password) delete payload.password;

      const result = isEditing
        ? await update(formModal.data.id, payload)
        : await create(payload);

      if (result.success) {
        toast.success(isEditing ? "Usuario actualizado" : "Usuario creado", {
          icon: "✅",
          duration: 2500,
        });
        formModal.close();
      } else {
        toast.error(result.error || "No se pudo guardar el usuario", {
          icon: "❌",
          duration: 5000,
          style: { maxWidth: "520px" },
        });
      }
    } catch (err) {
      toast.error("Error inesperado. Intentá nuevamente.", { icon: "❌", duration: 5000 });
    } finally {
      toast.dismiss(loadingToast);
    }
  };

  const handleDelete = async (usuario) => {
    const confirmed = await confirmAsync(`¿Seguro que deseas desactivar a ${usuario.username}?`);
    if (!confirmed) return;

    const loadingToast = toast.loading("Desactivando usuario...", {
      style: { borderLeft: `4px solid ${primaryColor}` },
    });

    try {
      const result = await remove(usuario.id);

      if (result.success) {
        toast.success("Usuario desactivado", { icon: "✅", duration: 2500 });
      } else {
        toast.error(result.error || "No se pudo desactivar el usuario", { icon: "❌", duration: 5000 });
      }
    } catch (err) {
      toast.error("Error inesperado. Intentá nuevamente.", { icon: "❌", duration: 5000 });
    } finally {
      toast.dismiss(loadingToast);
    }
  };

  // -------- UI states (DESPUÉS de hooks)
  if (loading && usuarios.length === 0) return <Loading primaryColor={primaryColor} />;
  if (error) return <ErrorMessage message={error} onRetry={reload} />;

  return (
    <div className="space-y-4">
      <Card
        primaryColor={primaryColor}
        title="Usuarios"
        subtitle="Administrá usuarios, datos y suscripciones"
        actions={
          <div className="flex items-center gap-3">
            <span className="hidden sm:inline text-xs text-slate-400">{resultsLabel}</span>
            <Button variant="primary" onClick={handleCreate} primaryColor={primaryColor}>
              Nuevo usuario
            </Button>
          </div>
        }
      >
        {/* Search */}
        <div className="mb-5">
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <svg className="w-5 h-5 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
            </div>

            <input
              type="text"
              placeholder="Buscar por usuario, nombre, email o teléfono..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-10 py-3 rounded-xl bg-slate-800/40 border border-slate-700 text-white placeholder-slate-500
                         focus:ring-2 focus:border-transparent transition"
              style={{ "--tw-ring-color": primaryColor }}
            />

            {searchTerm && (
              <button
                type="button"
                onClick={() => setSearchTerm("")}
                className="absolute inset-y-0 right-0 pr-3 flex items-center text-slate-400 hover:text-white"
                aria-label="Limpiar búsqueda"
              >
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                  <path
                    fillRule="evenodd"
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                    clipRule="evenodd"
                  />
                </svg>
              </button>
            )}
          </div>

          <div className="mt-2 text-xs text-slate-400">
            {searchTerm.trim() ? (
              usuariosFiltrados.length === 0 ? (
                <span className="text-red-300">No se encontraron usuarios</span>
              ) : (
                <span>{resultsLabel}</span>
              )
            ) : (
              <span>{resultsLabel}</span>
            )}
          </div>
        </div>

        {/* Table */}
        <Table
          rowKey="id"
          columns={columns}
          data={usuariosFiltrados}
          primaryColor={primaryColor}
          onRowClick={(row) => handleEdit(row)}
          emptyMessage={searchTerm ? `No se encontraron usuarios con "${searchTerm}"` : "No hay usuarios registrados"}
        />
      </Card>

      {/* Modal */}
      <Modal
        isOpen={formModal.isOpen}
        onClose={formModal.close}
        title={isEditing ? "Editar usuario" : "Nuevo usuario"}
        primaryColor={primaryColor}
      >
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Input
              label="Usuario"
              name="username"
              value={formData.username}
              onChange={handleInputChange}
              error={formErrors.username}
              required
              disabled={isEditing}
              primaryColor={primaryColor}
            />

            <Input
              label="Email"
              name="email"
              type="email"
              value={formData.email}
              onChange={handleInputChange}
              error={formErrors.email}
              primaryColor={primaryColor}
            />

            <Input
              label="Nombre"
              name="first_name"
              value={formData.first_name}
              onChange={handleInputChange}
              primaryColor={primaryColor}
            />

            <Input
              label="Apellido"
              name="last_name"
              value={formData.last_name}
              onChange={handleInputChange}
              primaryColor={primaryColor}
            />

            <Input
              label="DNI"
              name="dni"
              value={formatDni(formData.dni)}
              onChange={handleInputChange}
              error={formErrors.dni}
              required
              placeholder="12.345.678"
              primaryColor={primaryColor}
            />

            <Input
              label="Teléfono"
              name="telefono"
              value={formData.telefono}
              onChange={handleInputChange}
              placeholder="+549..."
              primaryColor={primaryColor}
            />

            <Input
              label={isEditing ? "Nueva contraseña (opcional)" : "Contraseña"}
              name="password"
              type="password"
              value={formData.password}
              onChange={handleInputChange}
              error={formErrors.password}
              required={!isEditing}
              primaryColor={primaryColor}
            />
          </div>

          <div className="flex justify-end gap-3 pt-4 border-t border-slate-700">
            <Button variant="ghost" onClick={formModal.close} type="button" disabled={submitting}>
              Cancelar
            </Button>
            <Button variant="primary" type="submit" disabled={submitting} primaryColor={primaryColor}>
              {submitting ? "Guardando..." : isEditing ? "Actualizar" : "Crear usuario"}
            </Button>
          </div>
        </form>
      </Modal>
    </div>
  );
}
